/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { Provider, Viewport } from "@radix-ui/react-toast";
import type { ComponentProps, Dispatch, SetStateAction } from "react";
import { createContext, useContext, useMemo, useState } from "react";

import {
  numericPercentages,
  numericPixels,
  spacing,
  zIndices,
} from "../../../global/emotion";

export type ToastProviderProps = ComponentProps<typeof Provider>;

const viewportStyle: Interpolation = {
  bottom: spacing.m,
  display: "flex",
  flexDirection: "column",
  height: "auto",
  left: numericPercentages[50],
  maxWidth: "400px",
  placeContent: "center",
  placeItems: "center",
  position: "fixed",
  rowGap: spacing.m,
  transform: `translateX(-50%)`,
  transition: "bottom 250ms ease-out",
  width: `calc(100vw - ${spacing.m})`,
  zIndex: zIndices.toast,
};
const withTabBarStyle: Interpolation = {
  bottom: `calc(${spacing.xxl} + ${spacing.m})`,
};
const withPlayerStyle: Interpolation = {
  bottom: `calc(${numericPixels[96]} + ${spacing.m})`,
};
const withBothStyle: Interpolation = {
  bottom: `calc(${spacing.xxl} + ${numericPixels[64]} + ${spacing.m})`,
};

const toastContext = createContext<{
  setHasPlayer: Dispatch<SetStateAction<boolean>>;
  setHasTabBar: Dispatch<SetStateAction<boolean>>;
}>({ setHasPlayer: () => {}, setHasTabBar: () => {} });

export const useToastPositioning = () => useContext(toastContext);

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const t = useTranslations();
  const [hasPlayer, setHasPlayer] = useState<boolean>(false);
  const [hasTabBar, setHasTabBar] = useState<boolean>(false);

  const styleMemo = useMemo(() => {
    const style: Interpolation[] = [viewportStyle];
    if (hasPlayer && hasTabBar) style.push(withBothStyle);
    else if (hasPlayer) style.push(withPlayerStyle);
    else if (hasTabBar) style.push(withTabBarStyle);

    return style;
  }, [hasPlayer, hasTabBar]);

  return (
    <toastContext.Provider value={{ setHasPlayer, setHasTabBar }}>
      <Provider label={t("web_toast_section_description")}>
        {children}

        <Viewport label={t("web_toast_section_description")} asChild>
          <ol css={styleMemo} />
        </Viewport>
      </Provider>
    </toastContext.Provider>
  );
};
